import style from "@/styles/pages/CoApplicant/CoApplicant.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    selectHomeAddress,
} from "@/slices/homeAddressSlice";
import { setMortgageComplete } from "@/slices/progressSlice";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import { selectPreviousAddress } from "@/slices/PreviousAddressSlice";
import { removeCoapplicant, selectCoApplicant, coapplicant, setCoapplicantList } from "@/slices/coApplicantSlice";
import React from "react";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { setApplicationProgress } from "@/slices/applicationSlice";
import { Toast } from "primereact/toast";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors, transformMonth } from "@/lib/util";

export default function CoApplicant() {
    const navigate = useNavigate();

    const dispatch = useDispatch();

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const homeAddress = useSelector(selectHomeAddress);
    const previousAddresses = useSelector(selectPreviousAddress).addresses;
    const coapplicants = useSelector(selectCoApplicant).coapplicants;
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const employment = useSelector(
        (state: RootState) => state.employment.employment
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const assets = useSelector((state: RootState) => state.assets.assets);
    const infoToast = useRef<Toast>(null);

    const methods = useForm({
        defaultValues: {
            autoAddress: "",
        },
    });

    const confirmDeletion = (id: string) => {

        //Check if the co-applicant was assigned an asset; if so, return an error
        let coapplicant = coapplicants.find((c) => c.id == id);
        let assetExists = false;
        assets.forEach((a) => {
            if (a.applicants.includes(coapplicant?.applicantId!)) {
                assetExists = true;
            }
        });
        if (assetExists) {
            infoToast.current?.show({ severity: 'error', summary: 'An asset has been listed for this co-applicant. Please remove the asset before removing the co-applicant.', life: 10000 });
            return;
        }

        confirmDialog({
            message: <p style={{ marginLeft: -14 }}>Are you sure you would like to remove this applicant?</p>,
            header: <h2 className="flex"><img src="assets/error-icon.svg" style={{ width: 24, marginRight: 12 }} />Remove Applicant?</h2>,
            defaultFocus: 'reject',
            acceptClassName: style.confirm,
            acceptLabel: "Remove applicant",
            rejectClassName: style.cancel,
            rejectLabel: "Cancel",
            accept: () => {
                //Remove Co-Applicant
                let coapplicant = coapplicants.find((c) => c.id == id);
                client.apiCoapplicantsDelete(coapplicant?.inviteCode!).then((result) => {

                    dispatch(removeCoapplicant(id));
                    updateCoapplicantIds(id);

                }).catch((error) => {
                    console.log("Error deleting co-applicant", error);
                });
            },
            draggable: false,
            closable: false
        });
    };

    useEffect(() => {
        document.title = "Add a Co-Applicant";
        //*set profile section complete
        dispatch(setMortgageComplete(true));
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);
    }, []);

    useEffect(() => {
        client.apiApplicationsFetch().then((result) => {
            result.coapplicants = coapplicants;
            result.previousAddresses = previousAddresses;
            result.homeAddress = homeAddress.address;
            result.application = application;
            result.applicant = applicant;
            result.employment = employment;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).then(() => {
                navigate("/single/co-applicant", { replace: true });
            }).catch((error) => {
                console.log("Error saving application info");
                navigate("/single/co-applicant", { replace: true });
            });
        }).catch((error) => {
            console.log("Error fetching application info");
            navigate("/single/co-applicant", { replace: true });
        });
    }, [coapplicants]);

    const updateCoapplicantIds = (excludeId: string) => {

        //Adjust IDs of other coapplicants
        let filteredList: coapplicant[] = [];
        coapplicants.forEach((c, index) => {
            if (c.id != excludeId) {
                filteredList.push(c);
            }
        });
        let newList: coapplicant[] = [];
        filteredList.forEach((c, index) => {
            let coapplicantInfo: coapplicant = {
                id: index + 1 + "",
                applicantId: c.applicantId,
                relationship: c.relationship,
                firstName: c.firstName,
                lastName: c.lastName,
                dob_year: c.dob_year,
                dob_month: c.dob_month,
                dob_day: c.dob_day,
                phoneNumber: c.phoneNumber,
                email: c.email,
                inviteCode: c.inviteCode,
                isInviteActive: c.isInviteActive
            }
            newList.push(coapplicantInfo);
        });
        dispatch(setCoapplicantList(newList));
    };

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = (data: any) => {
        dispatch(setApplicationProgress(8));
        setClickInsightApplicationProgress('Add your co-applicants');
        navigate("/single/address");
    };

    const addCoApplicant = () => {
        navigate("/single/add-co-applicant");
    }

    const editCoapplicant = (id: string) => {
        navigate('/single/add-co-applicant', { state: { id: id } });
    }

    const coapplicantList = () => {
        return coapplicants.map((position: any) => {
            return (
                <React.Fragment key={position.id}>
                    <div className="col-span-1 mb-4">
                        <div className={""}>
                            <div className={clsx("mt-5", style.pdContainer)}>
                                <div className="flex flex-row justify-between mt-5">
                                    <h2>Co-Applicant {position.id}</h2>
                                    {position.isInviteActive && <h3 className="brand-blue-text text-xs grid grid-flow-col cursor-pointer" style={{ marginTop: 4 }}>
                                        <span onClick={() => {
                                            editCoapplicant(position.id);
                                        }} style={{ marginRight: 12, marginLeft: 40 }}>Edit</span>
                                        <span onClick={() => {
                                            confirmDeletion(position.id)
                                        }}>Remove</span>
                                    </h3>}
                                </div>

                                <div className="flex md:flex-row flex-col">
                                    <div className={style.threeColFirst}>
                                        <p className="mt-5 text-sm">Full name</p>
                                        <h3 className="mt-3" style={{ wordWrap: 'break-word' }}>{position.firstName} {position.lastName}</h3>
                                    </div>
                                    <div className={style.threeColFirst}>
                                        <p className="mt-5 text-sm">Date of birth</p>
                                        <h3 className="mt-3" style={{ wordWrap: 'break-word' }}>{position.dob_day} {transformMonth(position.dob_month)}{" "}
                                            {position.dob_year}</h3>
                                    </div>
                                </div>
                                <div className="flex md:flex-row flex-col">
                                    <div className={style.threeColFirst}>
                                        <p className="mt-5 text-sm">Phone number</p>
                                        <h3 className="mt-3" style={{ wordWrap: 'break-word' }}>{position.phoneNumber}</h3>
                                    </div>
                                    <div className={style.threeColFirst}>
                                        <p className="mt-5 text-sm">Email address</p>
                                        <h3 className="mt-3">{position.email}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            );
        });
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <ConfirmDialog />
                    <Toast ref={infoToast} />
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <h1 aria-label="Address Header">Are you applying with a co-applicant?</h1>
                        <p className="sub-heading">You can add up to three additional borrowers. We will send them an email invitation once you submit this application.</p>
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className={style.sectionContainer}>

                                        {coapplicants != undefined && coapplicants.length > 0 &&
                                            <>
                                                {coapplicantList()}
                                            </>

                                        }

                                        {coapplicants != undefined && coapplicants.length < 3 && <div className="mb-5">
                                            <button
                                                type="button"
                                                onClick={addCoApplicant}
                                                className="secondary mt-5 w-full sm:w-28 h-11 px-6"
                                                style={{ width: '-webkit-fill-available', color: 'var(--brand-blue)', height: 56 }}>
                                                <img
                                                    className="mr-3"
                                                    style={{ marginBottom: 2 }}
                                                    src="assets/add-circle.svg"
                                                    alt="tooltip"
                                                    width={15}
                                                    height={15}
                                                />
                                                Add co-applicant
                                            </button>
                                        </div>
                                        }
                                    </div>
                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                style={{ minWidth: 150, textWrap: "nowrap" }}
                                            >
                                                {coapplicants.length == 0 && "No Thanks"}
                                                {coapplicants.length > 0 && "Save and Continue"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                {coapplicants.length == 0 && "No Thanks"}
                                                {coapplicants.length > 0 && "Save and Continue"}
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}